import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import MUIRichTextEditor from "c8-mui-rte";
import { TAutocomplete, TAutocompleteStrategy, TMUIRichTextEditorRef } from "c8-mui-rte/src/MUIRichTextEditor";
import { TAutocompleteItem } from "c8-mui-rte/src/components/Autocomplete";
import { TCustomControl, TToolbarComponentProps } from "c8-mui-rte/src/components/Toolbar";
import { getSelectionInfo } from "c8-mui-rte/src/utils";
import { routerStore } from "csi/state/rootStore";
import { addHttpProtocolIfMissing } from "csi/utility/routerUtility";
import Tooltip from "designSystem/components/tooltip/Tooltip";
import { Checkbox, OverflowText } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { black, grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { ContentState, EditorState, Modifier, RichUtils, SelectionState, convertFromRaw, convertToRaw } from "draft-js";
import { OrderedSet } from "immutable";
import React, { FunctionComponent, SyntheticEvent, useEffect, useRef, useState } from "react";
import * as api from "../api";
import LinkPopup, { LinkPopupProps } from "./LinkPopup";

const PREFIX = "MessageEditor";

const classes = {
	container: `${PREFIX}container`,
	editor: `${PREFIX}editor`,
	toolbar: `${PREFIX}toolbar`,
	autocomplete: `${PREFIX}autocomplete`,
};

const Root = styled("div")(({
	theme,
}) => ({
	[`& .${classes.container}`]: {
		margin: 0,
		padding: 0,
	},

	[`& .${classes.editor}`]: {
		color: black[100],
		fontSize: "12px",
		lineHeight: "1.4167em",
		maxHeight: "296px",
		minHeight: "44px",
		overflow: "auto",
		"& a.MuiLink-root": {
			display: "inline",
			overflow: "unset",
		},
		"& blockquote": {
			color: black[100],
			fontStyle: "normal",
			margin: 0,
			paddingLeft: theme.spacing(2),
		},
		"& pre": {
			margin: 0,
			padding: 0,
			whiteSpace: "pre-wrap",
		},
	},

	[`& .${classes.toolbar}`]: {
		backgroundColor: grey[100],
		borderBottom: STANDARD_BORDER_STYLE,
		padding: theme.spacing(1, 1.5),
		"& button.MuiIconButton-root": {
			color: grey.A100,
			margin: theme.spacing(1, 0.5),
			padding: "5px",
			transition: "none",
		},
		"& button#mui-rte-c8-appendId-button svg": {
			verticalAlign: "super",
		},
		"& button.MuiIconButton-root:not(#mui-rte-c8-appendId-button):hover": {
			backgroundColor: grey[400],
			borderRadius: "3px",
		},
		"& button.MuiIconButton-root:not(#mui-rte-c8-appendId-button):active": {
			backgroundColor: grey[400],
			borderRadius: "3px",
		},
		"& button.MuiIconButton-root.MuiIconButton-colorPrimary": {
			color: black[400],
			backgroundColor: grey[400],
			borderRadius: "3px",
		},
		"& button.MuiIconButton-root.Mui-disabled": {
			color: grey[500],
		},
		"& span#mui-rte-sendId-button-spacer": {
			marginLeft: "calc(100% - 30px * 6)",
		},
		"& button#mui-rte-sendId-button": {
			color: theme.palette.PrimaryDefault,
		},
		"& button#mui-rte-sendId-button.Mui-disabled": {
			color: grey[500],
		},
	},

	[`& .${classes.autocomplete}`]: {
		border: STANDARD_BORDER_STYLE,
		borderRadius: "3px",
		boxShadow: "0px 1px 3px rgba(0,0,0,0.15)",
		paddingBottom: theme.spacing(1),
		paddingTop: theme.spacing(1),
		"& li.MuiListItem-gutters": {
			paddingLeft: theme.spacing(2),
			paddingRight: theme.spacing(2),
		},
		"& li.MuiListItem-dense": {
			paddingBottom: theme.spacing(1),
			paddingTop: theme.spacing(1),
		},
		"& li.Mui-selected": {
			backgroundColor: grey[200],
		},
		"& div.MuiListItemAvatar-root": {
			minWidth: "unset",
			paddingRight: theme.spacing(1),
		},
		"& div.MuiAvatar-root": {
			borderRadius: "5px",
		},
	},
}));

const emptyMessage = (): string => {
	const emptyState = ContentState.createFromText("");
	return JSON.stringify(convertToRaw(emptyState));
};

const isEmptyMessage = (message: string): boolean => {
	const state = convertFromRaw(JSON.parse(message));
	return state.getBlocksAsArray()
		.map((b) => b.getText())
		.find((t) => t.trim().length > 0) === undefined;
};

const MENTION_STYLE = "MENTION-STYLE";

const handleMentionAutoComplete = (editorState: EditorState, selectionState: SelectionState, value: any): EditorState => {
	// preserve styles to restore them later
	const inlineStyle = editorState.getCurrentInlineStyle();

	// insert an autocompleted item with a special style
	const currentContentState = editorState.getCurrentContent();
	const entityKey = currentContentState.createEntity("MENTION", "IMMUTABLE", { value: value.id })
		.getLastCreatedEntityKey();
	const contentState = Modifier.replaceText(
		editorState.getCurrentContent(),
		selectionState,
		value.name,
		OrderedSet.of(MENTION_STYLE),
		entityKey,
	);
	const newEditorState = EditorState.push(editorState, contentState, "insert-characters");

	// add the space after autocompleted selection with restored styles
	const addSpaceState = Modifier.insertText(
		newEditorState.getCurrentContent(),
		newEditorState.getSelection(), " ",
		inlineStyle,
	);
	return EditorState.push(newEditorState, addSpaceState, "insert-characters");
};

const handleBlockStyleShortcut = (editorState: EditorState, selectionState: SelectionState, value: string): EditorState => {
	if (!value) {
		return editorState;
	}

	const contentState = Modifier.removeRange(editorState.getCurrentContent(), selectionState, "forward");
	editorState = EditorState.push(editorState, contentState, "remove-range");
	editorState = RichUtils.toggleBlockType(editorState, value);
	editorState = EditorState.forceSelection(editorState, editorState.getCurrentContent()
		.getSelectionAfter());
	return editorState;
};

function createAutoCompleteStrategies(usersJson: api.UsersList): TAutocomplete {
	const strategies: TAutocompleteStrategy[] = [{
		// [* + space] shortcut to switch current block style to the bullet list item
		items: [{
			keys: [""],
			value: "unordered-list-item",
			content: "",
		}],
		triggerChar: "*",
		minSearchChars: 1,
		handleAutoComplete: handleBlockStyleShortcut,
	},
	{
		// [1. + space] shortcut to switch current block style to the numbered list item
		items: [{
			keys: ["."],
			value: "ordered-list-item",
			content: "",
		}],
		triggerChar: "1",
		minSearchChars: 2,
		matchAutocompleteKey: (s, k) => (s === k),
		handleAutoComplete: handleBlockStyleShortcut,
	}];

	if (usersJson) {
		const users: TAutocompleteItem[] = usersJson.members.map((user) => {
			const displayableUserName = api.getDisplayableUserName(user.profile);
			const userAvatarImg = user.profile?.image48;
			return {
				keys: [
					user.name?.toLocaleLowerCase(),
					user.realName?.toLocaleLowerCase(),
					displayableUserName?.toLocaleLowerCase(),
				].filter(Boolean),
				value: { id: user.id, name: ("@" + displayableUserName) },
				content: (
					<>
						<ListItemAvatar>
							<Avatar alt={displayableUserName} src={userAvatarImg} />
						</ListItemAvatar>
						<ListItemText primary={displayableUserName} secondaryTypographyProps={{ component: "div" }} />
					</>
				),
			};
		})
			.sort((a, b) => a.value.name.localeCompare(b.value.name));

		const mentionStrategy =
		{
			items: users,
			triggerChar: "@",
			minSearchChars: 0,
			matchAutocompleteKey: (s, k) => k.startsWith(s.toLocaleLowerCase()),
			handleAutoComplete: handleMentionAutoComplete,
		};
		strategies.push(mentionStrategy);
	}
	return { strategies: strategies, suggestLimit: 20 };
}

interface Props {
	/** callback when check/uncheck the append checkbox */
	onCheckAppendCallback?: (append: boolean) => void;

	onSend: (message: String) => void;
	users: api.UsersList;
}

const MessageEditor: FunctionComponent<Props> = ({ onCheckAppendCallback, onSend, users }) => {
	const theme = useTheme();

	const locale = useLocale();
	const [message, setMessage] = useState<string>(emptyMessage);
	const [defaultMessage, setDefaultMessage] = useState<string>(emptyMessage);
	const [selectionEmpty, setSelectionEmpty] = useState<boolean>(true);
	const [autocomplete, setAutocomplete] = useState<TAutocomplete>(() => createAutoCompleteStrategies(users));
	const [showToolbar, setShowToolbar] = useState<boolean>(false);
	const [linkPopupProps, setLinkPopupProps] = useState<LinkPopupProps>(null);
	const [expandToolbar, setExpandToolbar] = useState<boolean>(false);
	const [append, setAppend] = useState<boolean>(true);

	const muiRteRef = useRef<TMUIRichTextEditorRef>(null);

	useEffect(() => setAutocomplete(createAutoCompleteStrategies(users)), [users]);

	const handleSave = (userMessage: string) => {
		if (!isEmptyMessage(userMessage)) {
			onSend(userMessage);
		}
	};

	const handleChange = (editorState: EditorState) => {
		const draftjs = convertToRaw(editorState.getCurrentContent());
		const userMessage = JSON.stringify(draftjs);
		setMessage(userMessage);
		setSelectionEmpty(isSelectionEmpty(editorState));
	};

	const isSelectionEmpty = (editorState: EditorState) => {
		const selection = editorState?.getSelection();
		if (null == selection) {
			return true;
		}
		return selection.getStartKey() === selection.getEndKey()
			&& selection.getStartOffset() === selection.getEndOffset();
	};

	function onFocus(e: SyntheticEvent) {
		setShowToolbar(true);
	}

	function onBlur(e: SyntheticEvent) {
		setShowToolbar(!isEmptyMessage(message));
	};

	/** handler for the append button */
	function onClickAppend() {
		setAppend(!append);
		onCheckAppendCallback?.(!append);
	}

	const handleLink = (editorState: EditorState, anchor: HTMLElement) => {
		const selectionInfo = getSelectionInfo(editorState);
		const contentState = editorState.getCurrentContent();
		const linkKey = selectionInfo.linkKey;
		let url = "";
		if (linkKey) {
			const linkInstance = contentState.getEntity(linkKey);
			url = linkInstance.getData()?.url;
		}
		if (!url && editorState.getSelection()
			.isCollapsed()) {
			return;
		}

		const onLinkSet = (url: string) => {
			setLinkPopupProps(null);
			muiRteRef.current.setLink(linkKey, addHttpProtocolIfMissing(url));
		};

		setLinkPopupProps({
			anchor,
			url,
			onOK: onLinkSet,
			onCancel: () => setLinkPopupProps(null),
		});
	};

	let controls = ["bold", "italic", "strikethrough", "numberList", "expand", "send"];

	if (expandToolbar) {
		controls = controls.concat(["bulletList", "quote", "c8-link", "code", "c8-append"]);
	} else {
		controls = controls.concat(["c8-append"]);
	}

	const LinkButton: FunctionComponent<TToolbarComponentProps> = ({ id, label, disabled, onMouseDown }) => {
		disabled ||= selectionEmpty;
		return (
			<span style={{ position: "relative" }}>
				<Tooltip
					aria-label={label}
					placement="top"
					title={label}
				>
					<span>
						<IconButton
							color={linkPopupProps ? "primary" : "default"}
							disabled={disabled}
							id={id}
							onMouseDown={onMouseDown}
							size="large"
						>
							<LinkIcon />
						</IconButton>
					</span>
				</Tooltip>
				{linkPopupProps ? <LinkPopup {...linkPopupProps} /> : null}
			</span>
		);
	};
	const ExpandButton: FunctionComponent<TToolbarComponentProps> = ({ id, label, disabled, onMouseDown }) => {
		return (
			<Tooltip
				aria-label={label}
				placement="top"
				title={label}
			>
				<span>
					<IconButton
						color={expandToolbar ? "primary" : "default"}
						disabled={disabled}
						id={id}
						onMouseDown={onMouseDown}
						size="large"
					>
						<MoreVertIcon />
					</IconButton>
				</span>
			</Tooltip>
		);
	};

	const SendButton: FunctionComponent<TToolbarComponentProps> = ({ id, label, onMouseDown }) => {
		const disabled = isEmptyMessage(message);

		return (
			<span id={id + "-spacer"}>
				<Tooltip
					aria-label={label}
					placement="top"
					title={label}
				>
					<span>
						<IconButton
							disabled={disabled}
							id={id}
							onMouseDown={onMouseDown}
							size="large"
						>
							<SendIcon />
						</IconButton>
					</span>
				</Tooltip>
			</span>
		);
	};

	const AppendCheckbox: FunctionComponent<TToolbarComponentProps> = ({ id, label, disabled, onMouseDown }) => {
		return (
			<IconButton
				color="default"
				id={id}
				onMouseDown={onMouseDown}
				size="large"
				sx={{
					justifyContent: "flex-start",
					width: 1,
				}}
			>
				<Checkbox checked={append} />
				<OverflowText
					px={1}
					res="AppendContextUrl"
					resParams={[routerStore.locationName]}
				/>
			</IconButton>
		);
	};

	const customControls: TCustomControl[] = [
		{
			name: "expand",
			label: "Expand",
			component: ExpandButton,
			type: "callback",
			onClick: () => setExpandToolbar(!expandToolbar),
		},
		{
			name: "send",
			label: "Send",
			component: SendButton,
			type: "callback",
			onClick: (editorState) => muiRteRef.current.save(),
		},
		{
			name: "c8-link",
			label: "Link",
			component: LinkButton,
			type: "callback",
			onPopup: (editorState, name, anchor) => handleLink(editorState, anchor),
		},
		{
			name: MENTION_STYLE,
			label: "Mention",
			icon: <div />,
			type: "inline",
			inlineStyle: {
				color: theme.palette.LinkDefault,
			},
		},
		{
			name: "c8-append",
			label: "",
			component: AppendCheckbox,
			type: "callback",
			onClick: () => onClickAppend(),
		},
	];

	return (
		<Root onFocus={onFocus}>
			<MUIRichTextEditor
				autocomplete={autocomplete}
				classOverrides={{
					autocomplete: classes.autocomplete,
					container: classes.container,
					editor: classes.editor,
					toolbar: classes.toolbar,
				}}
				controls={controls}
				customControls={customControls}
				defaultValue={defaultMessage}
				label="Post message"
				lineHeight={17}
				listMaxDepth={0}
				localization={(l) => locale.resource("SlackMessageEditorTT_" + l)}
				onBlur={onBlur}
				onChange={handleChange}
				onSave={handleSave}
				ref={muiRteRef}
				saveOnEnter
				toolbar={showToolbar}
			/>
		</Root>
	);
};

export default MessageEditor;